import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { c } from "../classroomColors";
import { STANDE } from "../standeTokens";
import { STATE, dateParts, stateTint } from "./spaceTokens";

/** Filled buttons, in the two kinds Stande uses — ink, and blue for the
 *  one thing on a screen that starts something — and the quiet one. */
export const inkButton = {
  bg: c.solidBg,
  color: c.solidText,
  _hover: { bg: c.solidBg, opacity: 0.86 },
  _active: { bg: c.solidBg, opacity: 0.8 },
};

export const blueButton = {
  bg: STANDE.primaryBlue,
  color: STANDE.black,
  fontWeight: "600",
  _hover: { bg: STANDE.primaryBlue, filter: "brightness(0.94)" },
  _active: { bg: STANDE.primaryBlue, filter: "brightness(0.9)" },
};

export const quietButton = {
  bg: c.surface,
  color: c.text,
  border: "1px solid",
  borderColor: c.borderSoft,
  _hover: { bg: c.hover },
  _active: { bg: c.hover },
};

/** A section of the space home: a white sheet on the page. */
export const Panel = ({ children, ...rest }) => (
  <Box bg={c.surface} border="1px solid" borderColor={c.borderSoft} borderRadius="18px" p="18px" {...rest}>
    {children}
  </Box>
);

export const PanelHead = ({ title, action, count }) => (
  <Flex align="center" justify="space-between" gap="10px" mb="12px">
    <Flex align="baseline" gap="8px">
      <Text fontSize="15px" fontWeight="600" color={c.text}>
        {title}
      </Text>
      {count != null && (
        <Text fontSize="12px" color={c.textMuted} sx={{ fontVariantNumeric: "tabular-nums" }}>
          {count}
        </Text>
      )}
    </Flex>
    {action}
  </Flex>
);

/** A session's state as a chip: a dot, and a tint where the state earns one. */
export const StateChip = ({ state, isDark, label }) => {
  const meta = STATE[state] || STATE.DRAFT;
  const tint = stateTint(state, isDark);
  return (
    <Flex
      as="span"
      display="inline-flex"
      align="center"
      gap="6px"
      px="9px"
      h="22px"
      borderRadius="200px"
      fontSize="11.5px"
      fontWeight="500"
      bg={tint || c.surfaceSubtle}
      color={c.text}
      flexShrink={0}
    >
      <Box boxSize="7px" borderRadius="full" bg={meta.dot} />
      {label || meta.label}
    </Flex>
  );
};

export const Chip = ({ children, tone }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    gap="5px"
    px="9px"
    h="22px"
    borderRadius="200px"
    fontSize="11.5px"
    bg={tone === "blue" ? c.infoSoft : c.surfaceSubtle}
    color={tone === "blue" ? c.infoText : c.textSub}
    whiteSpace="nowrap"
  >
    {children}
  </Box>
);

/** The day of a session, as a timetable shows it. No date: a dash. */
export const DateBox = ({ value, size = "md" }) => {
  const parts = dateParts(value);
  const big = size === "lg";
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      w={big ? "64px" : "52px"}
      h={big ? "70px" : "56px"}
      borderRadius="14px"
      bg={c.page}
      border="1px solid"
      borderColor={c.borderSoft}
      flexShrink={0}
      lineHeight="1.1"
    >
      {parts ? (
        <>
          <Text fontSize="10.5px" color={c.textMuted} textTransform="uppercase" letterSpacing="0.08em">
            {parts.weekday}
          </Text>
          <Text fontSize={big ? "22px" : "18px"} fontWeight="600" color={c.text} sx={{ fontVariantNumeric: "tabular-nums" }}>
            {parts.day}
          </Text>
          <Text fontSize="10.5px" color={c.textMuted} textTransform="uppercase" letterSpacing="0.08em">
            {parts.month}
          </Text>
        </>
      ) : (
        <Text fontSize="18px" color={c.textMuted}>
          –
        </Text>
      )}
    </Flex>
  );
};
